import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../../store'
import TaskList from '../tasks/TaskList'
import { phaseNeedsTaskLevel } from '../../lib/unlocks'

export default function PhaseList({ goalId }) {
  const createPhase   = useStore(s => s.createPhase)
  const updatePhase   = useStore(s => s.updatePhase)
  const completePhase = useStore(s => s.completePhase)
  const deletePhase   = useStore(s => s.deletePhase)
  const has           = useStore(s => s.has)
  const phases        = useStore(s => s.phases
    .filter(p => p.goal_id === goalId)
    .sort((a, b) => a.order_index - b.order_index)
  )

  const [adding,       setAdding]       = useState(false)
  const [newTitle,     setNewTitle]     = useState('')
  const [newWeeks,     setNewWeeks]     = useState('')
  const [newEffort,    setNewEffort]    = useState(1)
  const [completing,   setCompleting]   = useState(null)  // phase id
  const [actualWeeks,  setActualWeeks]  = useState('')
  const [expandedTask, setExpandedTask] = useState(null)  // phase id

  const handleAdd = async () => {
    if (!newTitle.trim()) return
    await createPhase(goalId, {
      title:           newTitle.trim(),
      estimated_weeks: newWeeks  ? Number(newWeeks)  : null,
      effort_level:    newEffort,
    })
    setNewTitle(''); setNewWeeks(''); setNewEffort(1); setAdding(false)
  }

  const handleComplete = async (phaseId) => {
    await completePhase(phaseId, actualWeeks ? Number(actualWeeks) : null)
    setCompleting(null); setActualWeeks('')
  }

  // Task level available for a phase?
  const taskLevelFor = (phase) => has('task_level') || phaseNeedsTaskLevel(phase)

  return (
    <div className="flex flex-col gap-3">

      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="label">Phases</span>
        {!adding && (
          <button onClick={() => setAdding(true)}
            className="font-mono text-[10px] text-m-accent hover:underline">
            + Add phase
          </button>
        )}
      </div>

      {/* Phase list */}
      <AnimatePresence>
        {phases.map((phase, idx) => (
          <motion.div key={phase.id} layout
            initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            className={`card-raised flex flex-col gap-0 overflow-hidden ${phase.status === 'completed' ? 'opacity-50' : ''}`}
          >
            {/* Phase row */}
            <div className="p-3 flex items-start gap-2">
              <span className="font-mono text-[10px] text-m-faint mt-0.5 w-5 shrink-0">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-m-text truncate">{phase.title}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  {phase.estimated_weeks && (
                    <span className="font-mono text-[10px] text-m-faint">{phase.estimated_weeks}w est.</span>
                  )}
                  {phase.actual_weeks && (
                    <span className="font-mono text-[10px] text-m-accent">{phase.actual_weeks}w actual</span>
                  )}
                  <span className={`font-mono text-[10px] ${
                    phase.status === 'completed'   ? 'text-m-accent' :
                    phase.status === 'in_progress' ? 'text-m-warn'   : 'text-m-faint'
                  }`}>
                    {phase.status}
                  </span>
                </div>
              </div>

              {/* Phase actions */}
              <div className="flex items-center gap-2 shrink-0">
                {phase.status !== 'completed' && (
                  <>
                    {/* Toggle tasks */}
                    {taskLevelFor(phase) && (
                      <button
                        onClick={() => setExpandedTask(expandedTask === phase.id ? null : phase.id)}
                        className={`font-mono text-[10px] transition-colors ${expandedTask === phase.id ? 'text-m-accent' : 'text-m-faint hover:text-m-subtle'}`}
                      >
                        tasks
                      </button>
                    )}

                    {completing === phase.id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number" min="0.5" step="0.5"
                          className="input-sm w-20 text-right"
                          placeholder="actual wks"
                          value={actualWeeks}
                          onChange={e => setActualWeeks(e.target.value)}
                        />
                        <button onClick={() => handleComplete(phase.id)}
                          className="font-mono text-[10px] text-m-accent hover:underline">
                          confirm
                        </button>
                        <button onClick={() => setCompleting(null)}
                          className="font-mono text-[10px] text-m-faint hover:text-m-subtle">
                          ×
                        </button>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => setCompleting(phase.id)}
                          className="font-mono text-[10px] text-m-subtle hover:text-m-accent transition-colors">
                          complete
                        </button>
                        <button onClick={() => deletePhase(phase.id)}
                          className="font-mono text-[10px] text-m-faint hover:text-m-danger transition-colors">
                          ×
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Task list (expanded) */}
            <AnimatePresence>
              {expandedTask === phase.id && taskLevelFor(phase) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden border-t border-m-border"
                >
                  <div className="p-3 pl-10">
                    <TaskList phaseId={phase.id} goalId={goalId} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Task level hint (disabled state) */}
            {!taskLevelFor(phase) && phase.status !== 'completed' && (
              <div className="px-3 pb-2 pl-10">
                <button disabled
                  title="Unlocks after 2+ committed goals with a phase"
                  className="font-mono text-[10px] text-m-faint opacity-40 cursor-not-allowed">
                  + Break into tasks
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Empty state */}
      {phases.length === 0 && !adding && (
        <p className="font-mono text-xs text-m-faint">
          No phases yet. Break this goal into phases to track execution.
        </p>
      )}

      {/* Add form */}
      <AnimatePresence>
        {adding && (
          <motion.div
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="card-raised p-3 flex flex-col gap-2 border-m-accent/30"
          >
            <input
              autoFocus
              className="input text-sm"
              placeholder="Phase title"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <div className="flex gap-2">
              <input
                type="number" min="1"
                className="input-sm w-24"
                placeholder="Weeks"
                value={newWeeks}
                onChange={e => setNewWeeks(e.target.value)}
              />
              <div className="flex gap-1 flex-1">
                {[[1,'Low'],[2,'Med'],[3,'High']].map(([v,l]) => (
                  <button key={v} onClick={() => setNewEffort(v)}
                    className={`flex-1 btn-sm text-[10px] py-1 ${newEffort === v ? 'border-m-accent text-m-accent' : 'border-m-border text-m-faint'}`}>
                    {l}
                  </button>
                ))}
              </div>
              <button onClick={handleAdd} className="btn-primary btn-sm">Add</button>
              <button onClick={() => { setAdding(false); setNewTitle(''); setNewWeeks('') }}
                className="btn-ghost btn-sm">×</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
